import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'
import { useAppStore } from './app'

const apiURL: string = import.meta.env.VITE_API_URL + '/api'

const api = axios.create({
  baseURL: apiURL + '/v1'
})

export interface PayrollSettings {
  uuid?: string

  // company info (printed in report headers)
  company_name: string
  address: string
  city: string
  tin: string
  phone: string
  email: string
  logo_url: string

  // pay rules
  default_pay_cycle: string
  hours_in_day: number
  break_hours: number
  days_in_year: number
  ot_factor: number
  np_factor: number
  rh_factor: number
  sh_factor: number
  nh_factor: number
  rd_factor: number
  govt_calc: string // 'basic salary' | 'basic + overtime'

  // feature switches
  enable_company: boolean
  enable_cost: boolean

  // report options
  prepared_by_name: string
  prepared_by_title: string
  approved_by_name: string
  approved_by_title: string
  bank_name: string
  report_footer: string
}

export const GOVT_CALC = {
  BasicSalary: 'basic salary',
  BasicOvertime: 'basic + overtime'
}

// What the app used before settings existed: the values compiled into app.ts and the
// VITE_* environment, so a database with no settings row behaves exactly as it did.
export const useSettingsStore = defineStore('settings', () => {
  const appStore = useAppStore()

  const defaults = (): PayrollSettings => ({
    company_name: '',
    address: '',
    city: '',
    tin: '',
    phone: '',
    email: '',
    logo_url: '',

    default_pay_cycle: '',
    hours_in_day: 8,
    break_hours: 1,
    days_in_year: 314,
    ot_factor: 1.0,
    np_factor: 0.1,
    rh_factor: 2.0,
    sh_factor: 1.0,
    nh_factor: 1.0,
    rd_factor: 1.0,
    govt_calc:
      import.meta.env.VITE_GOVT_CALC == GOVT_CALC.BasicSalary
        ? GOVT_CALC.BasicSalary
        : GOVT_CALC.BasicOvertime,

    enable_company: import.meta.env.VITE_ENABLE_COMPANY == 'false' ? false : true,
    enable_cost: import.meta.env.VITE_ENABLE_COST == 'true' ? true : false,

    prepared_by_name: '',
    prepared_by_title: '',
    approved_by_name: '',
    approved_by_title: '',
    bank_name: '',
    report_footer: ''
  })

  const settings = ref<PayrollSettings>(defaults())
  const loaded = ref(false)
  const saving = ref(false)

  // 'basic salary' leaves overtime and absences out of the SS / PhilHealth / Pagibig base
  const govtCalc = computed(() => settings.value.govt_calc || defaults().govt_calc)

  // Rows come back with nulls for anything never saved; keep the default instead.
  function merge(row: Partial<PayrollSettings> | undefined): PayrollSettings {
    const merged = defaults() as any
    for (const [key, value] of Object.entries(row ?? {})) {
      if (value !== null && value !== undefined) merged[key] = value
    }
    return merged as PayrollSettings
  }

  // Push the settings into the places the calculation and screens read from.
  function apply() {
    const s = settings.value
    appStore.options.companyEnabled = !!s.enable_company
    appStore.options.costEnabled = !!s.enable_cost

    appStore.payFactor.OT = Number(s.ot_factor)
    appStore.payFactor.NP = Number(s.np_factor)
    appStore.payFactor.RH = Number(s.rh_factor)
    appStore.payFactor.SH = Number(s.sh_factor)
    appStore.payFactor.NH = Number(s.nh_factor)
    appStore.payFactor.RD = Number(s.rd_factor)

    appStore.paySetting.hoursInDay = Number(s.hours_in_day)
    appStore.paySetting.breakHours = Number(s.break_hours)
    appStore.paySetting.daysInYear = Number(s.days_in_year)

    // only a starting point: the cycle the user last picked on this device wins
    let remembered: string | null = null
    try {
      remembered = localStorage.getItem('thisPayCycle')
    } catch {
      /* storage can be unavailable */
    }
    if (!remembered && s.default_pay_cycle) appStore.thisPayCycle = s.default_pay_cycle as any
  }

  async function load() {
    try {
      const response = await api.get('/payroll_settings.json')
      if (response.status == 200) {
        settings.value = merge(response.data?.data?.[0])
      }
    } catch {
      // keep the defaults; the app works without a settings row
    }
    loaded.value = true
    apply()
  }

  async function save(payload: PayrollSettings): Promise<boolean> {
    saving.value = true
    try {
      const body = { ...payload }
      // the API only lets an administrator's ticket change settings
      let ticket = ''
      try {
        ticket = localStorage.getItem('authTicket') || ''
      } catch {
        /* storage can be unavailable */
      }
      const headers = { 'X-Auth-Ticket': ticket }
      const response = payload.uuid
        ? await api.put('/payroll_settings/' + payload.uuid, body, { headers })
        : await api.post('/payroll_settings.json', body, { headers })

      if (response.status != 200 || response.data?.errors) return false
      // a created row comes back with its uuid; an update echoes what was sent
      settings.value = merge({ ...body, ...(response.data ?? {}) })
      apply()
      return true
    } catch {
      return false
    } finally {
      saving.value = false
    }
  }

  return { settings, loaded, saving, govtCalc, defaults, load, save, apply }
})
